import { Check, CheckCheck, Clock } from 'lucide-react';
import type { Message } from './types';
import { ImageWithFallback } from '../ImageWithFallback';

interface MessageBubbleProps {
  message: Message;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const schedule = message.schedule;
  // Schedule messages should always appear on the left (user side)
  const isAdmin = schedule ? false : message.isAdmin;

  // Support both camelCase and snake_case fields coming from backend
  const scheduleCustomerName =
    schedule?.customerName || schedule?.customer_name || null;

  const scheduleContactInfo =
    schedule?.contactInfo || schedule?.contact_info || null;

  // Derived schedule user info with sensible fallbacks
  const scheduleUserName =
    schedule &&
    (schedule.widgetUser?.firstName || schedule.widgetUser?.lastName)
      ? `${schedule.widgetUser?.firstName || ''} ${
          schedule.widgetUser?.lastName || ''
        }`.trim()
      : scheduleCustomerName || schedule?.widgetUser?.email || 'Guest';

  const scheduleUserContact =
    schedule?.widgetUser?.contact || scheduleContactInfo || '';

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Render read status icons (Telegram/WhatsApp style)
  const renderReadStatus = () => {
    if (!isAdmin) return null; // Only show for admin messages

    if (message.isRead) {
      // Read - Double check (blue)
      return (
        <CheckCheck className="h-3.5 w-3.5 text-blue-500 animate-in fade-in duration-200" />
      );
    } else {
      // Sent but not read - Single check (gray)
      return (
        <Check className="h-3.5 w-3.5 text-primary-foreground/60 animate-in fade-in duration-200" />
      );
    }
  };

  return (
    <div
      className={`flex animate-in slide-in-from-bottom-2 duration-300 ${
        isAdmin ? 'justify-end' : 'justify-start'
      }`}
    >
      <div
        className={`group relative max-w-[75%] md:max-w-[60%] rounded-2xl  transition-all  ${
          isAdmin
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-muted/80 backdrop-blur-sm text-foreground rounded-bl-md border border-border/50'
        }`}
      >
        {/* Message content */}
        <div className="px-3.5 pt-2.5 pb-1 space-y-1.5">
          {/* Primary text (we'll map information/question into text when needed) */}
          {message.text && (
            <p className="text-[13px] leading-[1.4] whitespace-pre-wrap break-words">
              {message.text}
            </p>
          )}

          {/* Generated image from AI */}
          {message.generated_photo && (
            <div className="mt-2 -mx-1 rounded-lg overflow-hidden">
              <img
                src={message.generated_photo}
                alt="Generated"
                className="w-full h-auto max-h-72 object-cover"
              />
            </div>
          )}

          {/* Images if any */}
          {message.images && message.images.length > 0 && (
            <div className="mt-2 -mx-1 grid grid-cols-2 gap-1.5">
              {message.images.map((img, idx) => (
                <div
                  key={idx}
                  className="relative rounded-lg overflow-hidden group-hover:scale-[1.02] transition-transform"
                >
                  <img
                    src={img}
                    alt={`attachment-${idx}`}
                    className="w-full h-auto max-h-48 object-cover"
                  />
                </div>
              ))}
            </div>
          )}

          {schedule && (
            <div className="mt-3 rounded-xl border min-w-[300px] border-blue-300/70 bg-blue-50/90 text-foreground p-4 space-y-3">
              {/* Header with prominent date/time */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-500 text-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide">
                  Schedule
                </span>
                <div className="flex items-center gap-2 text-right">
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-bold text-blue-900 leading-tight">
                      {new Date(schedule.bookedTime).toLocaleDateString(
                        'en-GB',
                        {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        }
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs font-semibold text-blue-700 mt-0.5">
                      <Clock className="h-3 w-3" />
                      {new Date(schedule.bookedTime).toLocaleTimeString(
                        'en-GB',
                        {
                          hour: '2-digit',
                          minute: '2-digit',
                        }
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 space-y-1.5">
                {schedule.branch?.name && (
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">Branch:</span>{' '}
                    <span>{schedule.branch.name}</span>
                  </div>
                )}
              </div>
              {schedule && schedule.product && (
                <div className="flex items-start gap-3">
                  {schedule.product?.images?.[0]?.url && (
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-blue-200/50 bg-muted">
                      <ImageWithFallback
                        isStorageUrl={
                          !schedule.product.images[0].url.startsWith('http')
                        }
                        src={schedule.product.images[0].url}
                        alt={schedule.product.name || 'Product image'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 space-y-1.5">
                    {schedule.product.name && (
                      <div className="font-semibold text-sm text-foreground line-clamp-2">
                        {schedule.product.name}
                      </div>
                    )}
                    {schedule.product?.sku && (
                      <span className="text-xs font-mono font-semibold text-blue-700 bg-blue-100/50 px-2 py-0.5 rounded">
                        SKU: {schedule.product.sku}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-blue-200/50 mt-2">
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">
                    {scheduleUserName}
                  </span>
                  {scheduleUserContact && (
                    <span className="ml-2 text-[11px] text-muted-foreground">
                      {scheduleUserContact}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Time and status footer */}
        <div
          className={`flex items-center justify-end gap-1 px-3.5 pb-1.5 text-[11px] ${
            isAdmin ? 'text-primary-foreground/60' : 'text-muted-foreground/80'
          }`}
        >
          <span className="font-medium">{formatTime(message.createdAt)}</span>
          {renderReadStatus()}
        </div>
      </div>
    </div>
  );
}
